'use client';

import { useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSettings } from '@/providers/settings-provider';
import { useOrganisationThemeContext } from '@/providers/organisation-theme-provider';
import { ExternalContentLoader } from '@/app/components/external-content-loader';
import { Footer } from './components/footer';
import { Header } from './components/header';
import { Sidebar } from './components/sidebar';
import { TelegramChatWidget } from '@/components/telegram/telegram-chat-widget';
import { useSidebarFix } from '@/components/ui/sidebar-fix-hook';
import { NexusGlobalStatusBar } from '@/app/components/nexus/nexus-global-status-bar';
import { LayoutBackgroundLayer } from '@/components/ui/layout-background-layer';

export function Demo1Layout({ children }) {
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const { settings, setOption } = useSettings();
  const bgKey = settings?.ui?.backgroundAnimation ?? 'particles';
  useSidebarFix(); // Fix duplicate sidebar issue

  // Get organisation theme settings
  const { theme } = useOrganisationThemeContext();

  // Sidebar mode from organisation theme (normal, collapsed, hidden)
  const sidebarMode = useMemo(() => {
    return theme?.layout?.sidebar?.mode || 'normal';
  }, [theme]);

  // Determine if sidebar should be shown
  const showSidebar = useMemo(() => {
    return !isMobile && sidebarMode !== 'hidden';
  }, [isMobile, sidebarMode]);

  // Developer-Projekt-Editor (/developer/projects/<id>): IDE-artiger
  // Fullheight-Workspace — Sidebar collapsed, Header + Footer ausgeblendet
  // (User-Wunsch 2026-06-11: Header fraß Viewport-Höhe, Workspace "zu tief").
  const isDeveloperProjectEditor = useMemo(
    () => /^\/developer\/projects\/\d+$/.test(pathname || ''),
    [pathname]
  );

  // Auto-collapse sidebar for developer project editor
  useEffect(() => {
    if (isDeveloperProjectEditor && !settings.layouts.demo1.sidebarCollapse) {
      setOption('layouts.demo1.sidebarCollapse', true);
    }
  }, [isDeveloperProjectEditor, settings.layouts.demo1.sidebarCollapse, setOption]);

  // Apply sidebar collapse state based on org theme or local settings
  useEffect(() => {
    const bodyClass = document.body.classList;

    // Organisation theme takes precedence
    if (sidebarMode === 'collapsed') {
      bodyClass.add('sidebar-collapse');
    } else if (sidebarMode === 'hidden') {
      bodyClass.add('sidebar-hidden');
      bodyClass.remove('sidebar-collapse');
    } else if (settings.layouts.demo1.sidebarCollapse) {
      bodyClass.add('sidebar-collapse');
    } else {
      bodyClass.remove('sidebar-collapse');
    }

    // Cleanup hidden class
    return () => {
      bodyClass.remove('sidebar-hidden');
    };
  }, [settings, sidebarMode]);

  useEffect(() => {
    // Set current layout
    setOption('layout', 'demo1');
  }, [setOption]);

  useEffect(() => {
    const bodyClass = document.body.classList;

    // Add a class to the body element
    bodyClass.add('demo1');
    bodyClass.add('sidebar-fixed');
    // header-fixed setzt padding-top: var(--header-height) auf .wrapper —
    // im Projekt-Editor (Header ausgeblendet) muss die Klasse mit weg,
    // sonst bleibt eine Header-hohe Lücke.
    if (isDeveloperProjectEditor) {
      bodyClass.remove('header-fixed');
    } else {
      bodyClass.add('header-fixed');
    }

    const timer = setTimeout(() => {
      bodyClass.add('layout-initialized');
    }, 1000); // 1000 milliseconds

    // Remove the class when the component is unmounted
    return () => {
      bodyClass.remove('demo1');
      bodyClass.remove('sidebar-fixed');
      bodyClass.remove('sidebar-collapse');
      bodyClass.remove('header-fixed');
      bodyClass.remove('layout-initialized');
      clearTimeout(timer);
    };
  }, [isDeveloperProjectEditor]);

  return (
    <>
      {showSidebar && <Sidebar />}

      <div className={`wrapper flex flex-col h-dvh overflow-hidden ${sidebarMode === 'hidden' ? 'sidebar-hidden-mode' : ''}`}>
        {!isDeveloperProjectEditor && <Header />}
        {/* Im Projekt-Editor keine Statusbar im Flow: sie schob den h-dvh-
            Workspace nach unten (Layout-Shift bei "Nexus Task aktiv"). Der
            Task-Status ist dort ohnehin im Chat sichtbar. */}
        {!isDeveloperProjectEditor && <NexusGlobalStatusBar />}

        <main
          className="flex-1 min-h-0 flex flex-col relative overflow-hidden"
          role="content"
        >
          {/* Background animation layer — fixed behind scrollable content */}
          <LayoutBackgroundLayer bgKey={bgKey} />

          {/* Scrollable content sits on top of the background */}
          <div className={`relative z-[1] flex-1 overflow-auto ${isDeveloperProjectEditor ? '' : 'pt-5'} flex flex-col`}>
            {/* External Content Loader - Wrapper für kompletten Content */}
            <ExternalContentLoader>
              {/* Standard Next.js Content - wird nur gezeigt wenn kein External Content */}
              {children}
            </ExternalContentLoader>
            {/* Telegram Chat Widget bleibt außerhalb des toggled Bereichs */}
            <TelegramChatWidget />
          </div>
        </main>

        {!isDeveloperProjectEditor && <Footer />}
      </div>
    </>
  );
}
